import { StandardEvents } from '@shopify/events';

/**
 * Bundle "1/2/3/4 housses" - fiche produit SofaGuard uniquement. Copie
 * independante adaptee de assets/product-bundle-selector.js (CleanyStep,
 * jamais touche ici) - voir snippets/sg-bundle-selector.liquid pour le
 * detail complet des differences (4 paliers, produits lies, ordre
 * option1/option2 inverse, source d'image native, prefixe sg-).
 *
 * Etat 100% local (palier + couleur par housse) jusqu'au clic "Ajouter au
 * panier" (voir sg-bundle-add-to-cart.js, qui lit getSelection() ci-dessous -
 * API publique identique en forme a la version CleanyStep, getSelection()/
 * tierData, pour que sg-bundle-add-to-cart.js reste un simple miroir de
 * cs-bundle-add-to-cart.js).
 *
 * Index de fieldset Couleur/Taille lus depuis les data-attributes
 * (data-color-fieldset-index / data-size-fieldset-index, calcules cote
 * Liquid par NOM d'option) plutot que codes en dur "0"/"1" - SofaGuard a
 * l'ordre d'options INVERSE de CleanyStep (Taille=option1, Couleur=
 * option2 - voir le tout premier bug corrige sur ce produit). Coder "0"/"1"
 * en dur ici aurait reproduit exactement ce meme piege une 2e fois.
 */
class SgBundleSelector extends HTMLElement {
  connectedCallback() {
    /** @type {{color: string, size: string, available: boolean}[]} */
    this.variants = this.#parseJsonAttr('variants') || [];
    /** @type {{[tier: string]: {[size: string]: {variantId?: string, priceFormatted: string, referencePriceFormatted?: string, savingsPercent?: number}}}} */
    this.tierData = this.#parseJsonAttr('tier-data') || {};

    this.colorFieldsetIndex = this.dataset.colorFieldsetIndex;
    this.sizeFieldsetIndex = this.dataset.sizeFieldsetIndex;

    this.maxTier = 4;
    /** @type {1|2|3|4} */
    this.tier = 1;
    /** @type {{[housseIndex: number]: string}} */
    this.housseColors = {
      1: this.dataset.defaultColor,
      2: this.dataset.defaultColor,
      3: this.dataset.defaultColor,
      4: this.dataset.defaultColor,
    };

    this.tierInputs = Array.from(this.querySelectorAll('[data-sg-tier-input]'));
    this.housseColorInputs = Array.from(this.querySelectorAll('[data-sg-housse-color-input]'));
    this.cardExpandedEls = Array.from(this.querySelectorAll('[data-sg-card-expanded]'));
    this.sizeIndicatorEls = Array.from(this.querySelectorAll('[data-sg-size-indicator]'));

    /** @type {{[housseIndex: string]: HTMLElement}} */
    this.housseRowsByIndex = {};
    this.querySelectorAll('.sg-bundle__housse-rows-source > [data-sg-housse]').forEach((row) => {
      if (row instanceof HTMLElement) this.housseRowsByIndex[row.dataset.sgHousse] = row;
    });
    /** @type {{[tier: string]: HTMLElement}} */
    this.housseRowsSlots = {};
    this.querySelectorAll('[data-sg-housse-rows-slot]').forEach((slot) => {
      if (slot instanceof HTMLElement) this.housseRowsSlots[slot.dataset.sgHousseRowsSlot] = slot;
    });

    this.tierInputs.forEach((input) => {
      input.addEventListener('change', () => this.#handleTierChange(Number(input.value)));
    });

    this.housseColorInputs.forEach((input) => {
      input.addEventListener('change', () => {
        const housseIndex = Number(input.dataset.sgHousse);
        this.housseColors[housseIndex] = input.value;
        this.#syncNativeColor(input.value);
        this.#scrollGalleryToColor(input.value);
      });
    });

    // Le vrai selecteur Couleur natif ne sert plus qu'a declencher, en
    // silence, la galerie/la barre de stock (meme mecanisme que CleanyStep) -
    // masque des le chargement, quel que soit le palier. Masquer ce
    // fieldset masque AUSSI, par construction, le libelle "Couleurs : X"
    // injecte a l'interieur par media_section_overrides (il en est un
    // enfant direct) - aucune logique separee necessaire pour ce libelle.
    const realColorFieldset = document.querySelector(
      `variant-picker fieldset[data-fieldset-index="${this.colorFieldsetIndex}"]`
    );
    if (realColorFieldset instanceof HTMLElement) realColorFieldset.hidden = true;

    this.#placeHousseRows(1);
    this.#updateAllPrices();

    this.#waitForVariantPicker((sizeFieldset) => {
      this.sizeFieldset = sizeFieldset;
      sizeFieldset.addEventListener('change', () => {
        this.#refreshAvailability();
        this.#updateAllPrices();
      });
      this.#refreshAvailability();
      this.#updateAllPrices();
    });

    document.addEventListener(StandardEvents.productSelect, (event) => {
      event.promise?.then(() => this.#updateAllPrices()).catch(() => {});
    });
  }

  /**
   * @param {number} tier
   */
  #placeHousseRows(tier) {
    const slot = this.housseRowsSlots[String(tier)];
    if (!slot) return;
    for (let i = 1; i <= tier; i++) {
      const row = this.housseRowsByIndex[String(i)];
      if (row) slot.appendChild(row);
    }
    Object.values(this.housseRowsByIndex).forEach((row) => {
      const label = row.querySelector('.sg-bundle__housse-row-label');
      if (label instanceof HTMLElement) label.hidden = tier === 1;
    });
  }

  /**
   * @param {string} name
   * @returns {any}
   */
  #parseJsonAttr(name) {
    const raw = this.dataset[name.replace(/-([a-z])/g, (_, c) => c.toUpperCase())];
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      console.warn('[sg-bundle-selector] JSON attribute parse failed:', name, error);
      return null;
    }
  }

  /**
   * @param {(fieldset: Element) => void} callback
   */
  #waitForVariantPicker(callback) {
    const tryFind = () =>
      document.querySelector(`variant-picker fieldset[data-fieldset-index="${this.sizeFieldsetIndex}"]`);
    const existing = tryFind();
    if (existing) {
      callback(existing);
      return;
    }
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      const fieldset = tryFind();
      if (fieldset) {
        clearInterval(interval);
        callback(fieldset);
      } else if (attempts >= 50) {
        clearInterval(interval);
      }
    }, 100);
  }

  /** @returns {string} The currently selected real size value. */
  #currentSize() {
    const checked = this.sizeFieldset?.querySelector('input:checked');
    return checked instanceof HTMLInputElement ? checked.value : this.dataset.defaultSize;
  }

  /**
   * @param {number} tier
   */
  #handleTierChange(tier) {
    const previousTier = this.tier;
    this.tier = tier;

    if (tier > previousTier) {
      for (let i = previousTier + 1; i <= tier; i++) {
        this.housseColors[i] = this.housseColors[1];
        const input = this.housseColorInputs.find(
          (el) => Number(el.dataset.sgHousse) === i && el.value === this.housseColors[1]
        );
        if (input instanceof HTMLInputElement) input.checked = true;
      }
    }

    this.cardExpandedEls.forEach((el) => {
      const cardTier = Number(el.dataset.sgCardExpanded);
      el.hidden = cardTier !== tier;
    });
    this.#placeHousseRows(tier);

    this.#syncNativeColor(this.housseColors[tier > previousTier ? tier : 1]);
    this.#refreshAvailability();
    this.#updateAllPrices();
  }

  /**
   * @param {string} colorValue
   */
  #syncNativeColor(colorValue) {
    const fieldset = document.querySelector(
      `variant-picker fieldset[data-fieldset-index="${this.colorFieldsetIndex}"]`
    );
    if (!fieldset) return;
    const input = fieldset.querySelector(`input[value="${CSS.escape(colorValue)}"]`);
    if (!(input instanceof HTMLInputElement) || input.checked) return;

    window.__csGallerySuppressRebuild = true;
    setTimeout(() => {
      window.__csGallerySuppressRebuild = false;
    }, 0);
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Fait defiler la galerie principale jusqu'a la photo de cette couleur
   * precise (demande explicite du marchand : revoir visuellement la housse
   * choisie en remontant en haut de page, sans devoir chercher). Reutilise
   * l'infrastructure deja existante et partagee (snippets/product-media-
   * gallery-content.liquid - "Gallery <-> color swatch sync (bidirectional)")
   * plutot que d'inventer un nouveau mecanisme : chaque diapo dont le media
   * est le featured_media d'un variant porte deja data-swatch-color="<valeur
   * couleur>" + slide-id="<media.id>". <media-gallery>.slideshow.select()
   * accepte directement {id: slideId} (voir assets/slideshow.js) - pas besoin
   * de calculer un index nous-memes, donc toujours correct meme apres le
   * reordonnancement cote SofaGuard (voir media_section_overrides,
   * "ordre naturel galerie").
   * @param {string} colorValue
   */
  #scrollGalleryToColor(colorValue) {
    const slide = document.querySelector(
      `media-gallery [data-swatch-color="${CSS.escape(colorValue)}"][slide-id]`
    );
    const slideId = slide?.getAttribute('slide-id');
    if (!slideId) return;

    const gallery = document.querySelector('media-gallery');
    gallery?.slideshow?.select({ id: slideId }, undefined, { animate: true });
  }

  #refreshAvailability() {
    const size = this.#currentSize();
    this.housseColorInputs.forEach((input) => {
      const entry = this.variants.find((v) => v.color === input.value && v.size === size);
      const available = entry ? entry.available : true;
      input.toggleAttribute('data-sg-unavailable', !available);
    });
  }

  /**
   * Met a jour le prix des 4 cartes + <product-price> natif - meme logique
   * que CleanyStep (voir product-bundle-selector.js), boucle etendue a 4
   * paliers.
   */
  #updateAllPrices() {
    const size = this.#currentSize();

    for (let tier = 1; tier <= this.maxTier; tier++) {
      const entry = this.tierData[String(tier)]?.[size];
      if (!entry) continue;

      const priceEl = this.querySelector(`[data-sg-card-price="${tier}"]`);
      if (priceEl) priceEl.textContent = entry.priceFormatted;

      const savingsEl = this.querySelector(`[data-sg-card-savings="${tier}"]`);
      const referenceEl = this.querySelector(`[data-sg-card-reference="${tier}"]`);
      if (savingsEl) savingsEl.textContent = typeof entry.savingsPercent === 'number' ? `Tu économises ${entry.savingsPercent}%` : '';
      if (referenceEl) referenceEl.textContent = entry.referencePriceFormatted || '';
    }

    const nativePrice = document.querySelector('product-price');
    const currentEntry = this.tierData[String(this.tier)]?.[size];
    if (nativePrice instanceof HTMLElement && currentEntry) {
      nativePrice.style.removeProperty('display');
      const container = nativePrice.querySelector('[ref="priceContainer"]') || nativePrice;
      const referenceHtml = currentEntry.referencePriceFormatted
        ? `<span class="price-item__group price-item--regular compare-at-price"><span class="visually-hidden">Prix régulier&nbsp;</span>${currentEntry.referencePriceFormatted}</span>`
        : '';
      const saleLabel = currentEntry.referencePriceFormatted ? 'Prix promotionnel' : 'Prix';
      const badgeHtml = typeof currentEntry.savingsPercent === 'number'
        ? `<span class="sg-price-badge">-${currentEntry.savingsPercent}%</span>`
        : '';
      container.innerHTML = `<span class="price-item__group price"><span class="visually-hidden">${saleLabel}&nbsp;</span>${currentEntry.priceFormatted}</span>${referenceHtml}${badgeHtml}`;
    }

    this.sizeIndicatorEls.forEach((el) => {
      el.textContent = `Taille sélectionnée : ${size}`;
    });
  }

  /**
   * Lu par sg-bundle-add-to-cart.js au moment de la soumission.
   * @returns {{tier: 1|2|3|4, size: string, colors: string[]}}
   */
  getSelection() {
    const colors = [];
    for (let i = 1; i <= this.tier; i++) colors.push(this.housseColors[i]);
    return { tier: this.tier, size: this.#currentSize(), colors };
  }
}

if (!customElements.get('sg-bundle-selector')) {
  customElements.define('sg-bundle-selector', SgBundleSelector);
}
